<script setup>

import { MapboxGeolocateControl, MapboxImages, MapboxMap, MapboxNavigationControl, MapboxPopup } from "@studiometa/vue-mapbox-gl";
import StadsnatuurMapLayer from "./StadsnatuurMapLayer.vue";
import { computed, onMounted, ref, watch } from 'vue';
import StadsnatuurMapFilters from "./StadsnatuurMapFilters.vue";
import scrollLock from 'scroll-lock';

const center = ref({ lat: 52, lng: 5 });
const accessToken = 'pk.eyJ1Ijoic2VwdGVtYmVyZGlnaXRhbCIsImEiOiJjbDM5NTMxaWcwNmVqM2RxbTgwYTZwc2R6In0.muheCHZVNeU2x8FHymhtuQ';
const mapStyle = 'mapbox://styles/mapbox/streets-v11';
const zoom = ref(7);


const selectedItemId = ref(null);
const selectMarker = (marker) => {
  selectedItemId.value = null;
  setTimeout(() => {
    selectedItemId.value = marker.properties.id;
  }, 10);
}



const items = ref([]);

onMounted(() => {
  fetch('/wp/wp-admin/admin-ajax.php?action=stadsnatuur_json')
    .then(res => res.json())
    .then((data) => {
      items.value = data || [];
    })
});

const map = ref(null);

const filters = ref({ types: [] });

const filteredItems = computed(() => {
  return items.value.filter((item) => {

    const filterValues = filters.value || {};

    for (let key of ['gratis', 'rolstoel', 'kinderen']) {
      if (filterValues[key]) {
        if (!item[key])
          return false;
      }
    }

    const types = filterValues.types || [];

    if (types.length > 0) {
      if (!types.find(type => item.types.includes(type))) {
        return false;
      }
    }

    return true;
  });
})

const selectedItem = computed(() => {
  return items.value.find(item => item.id == selectedItemId.value);
});

const types = {
  'sportive': 'Sportief',
  'cultural': 'Cultuur',
  'nature': 'Natuur',
  'excursion': 'Excursie',
  'workshop': 'Workshop',
  'walk': 'Wandeling',
  'lecture': 'Lezing',
  'diy': 'Doe-het-zelf',
};

let openFilters = ref(false);

function toggleFilters(state) {
  openFilters.value = state;

  if (openFilters.value) {
    scrollLock.disablePageScroll();
  } else {
    scrollLock.enablePageScroll();
  }
}

</script>

<template>
  <main class="map">
    <StadsnatuurMapFilters v-model="filters" :types="types" v-model:openFilters="openFilters"
      :toggleFilters="toggleFilters" />

    <div class="stadsnatuur-map-mapbox-wrapper">
      <MapboxMap style="height: 100%; border-radius: 7px;
          border: solid 1px #dadde2; overflow: hidden;" :accessToken="accessToken" :mapStyle.sync="mapStyle"
        :center.sync="center" :zoom.sync="zoom" :scrollZoom="false" :dragRotate="false" ref="map">
        <MapboxImages :sources="Object.keys(types).map(key => ({
          src: `/app/themes/natuur-en-milieu/campagnes/stadsnatuur/dist/images/${key}.png`,
          id: `stadsnatuur-${key}`,
        }))">


        </MapboxImages>

        <StadsnatuurMapLayer :items="filteredItems" :types="types" @select-marker="selectMarker" />

        <MapboxNavigationControl position="top-right" :showCompass="false" />
        <MapboxGeolocateControl :trackUserLocation="true" :positionOptions="{ enableHighAccuracy: true }"
          ref="tracker" />

        <MapboxPopup v-if="selectedItem" :lng-lat="[selectedItem.pos.lng, selectedItem.pos.lat]"
          @mb-close="selectedItemId = null">
          <h2 class="mapboxgl-popup-content__title">{{ selectedItem['title'] }}</h2>
          <div class="stadsnatuur-map-legend-items">
            <template v-for="(type, key) in types" :key="key">
              <div class="stadsnatuur-map-legend-item" v-if="selectedItem.types.includes(key)">
                <span class="stadsnatuur-map-legend-item__icon">
                  <svg class="icon icon-icon-marker">
                    <use :xlink:href="`#icon-marker-${key}`"></use>
                  </svg>
                </span>
                {{ type }}
              </div>
            </template>

            <div class="stadsnatuur-map-legend-item" v-if="selectedItem.gratis">
              <span class="c-small-page-header__activity-price-icon">
                <svg class="icon icon-euro">
                  <use xlink:href="#icon-euro"></use>
                </svg>
              </span>
              Gratis
            </div>

            <div class="stadsnatuur-map-legend-item" v-if="selectedItem.kinderen">
              <svg class="icon icon-checkbox">
                <use xlink:href="#icon-checkbox"></use>
              </svg>
              Kindvriendelijk
            </div>

            <div class="stadsnatuur-map-legend-item" v-if="selectedItem.rolstoel">
              <svg class="icon icon-checkbox">
                <use xlink:href="#icon-checkbox"></use>
              </svg>
              Rolstoelvriendelijk
            </div>
          </div>
          <!-- <p class="mapboxgl-popup-content__text" v-html="selectedItem.text"></p> -->

          <a :href="selectedItem.link" class="mapboxgl-popup-content__link">
            Bekijk activiteit
            <svg class="icon icon-arrow-right">
              <use xlink:href="#icon-arrow-right"></use>
            </svg>
          </a>
        </MapboxPopup>

      </MapboxMap>

      <button class="stadsnatuur-map-filters__button" @click="toggleFilters(true)">
        Filters
      </button>
    </div>

    <div class="stadsnatuur-map-legend-wrapper">
      <span class="stadsnatuur-map-legend__title">
        Legenda
      </span>
      <ul class="stadsnatuur-map-legend">
        <li class="stadsnatuur-map-legend-item" v-for="(type, key) of types">
          <span class="stadsnatuur-map-legend-item__icon">
            <svg slot="marker" class="icon icon-icon-marker">
              <use :xlink:href="`#icon-marker-${key}`"></use>
            </svg>
          </span>
          {{ type }}
        </li>
      </ul>
    </div>
  </main>
</template>

<style scoped>
.map {
  width: 100vw;
  height: 500px;
  position: relative;
}
</style>